/**
 * The pure halves of the shipped page adapters (Issue #12948).
 *
 * Each function here turns data a page ALREADY has into a workspace surface.
 * They live in `src/lib/` rather than beside their pages so the interesting
 * part — what gets published, and what deliberately does not — is unit
 * testable without rendering anything, and so the three worked examples sit
 * next to each other for the next surface to copy from.
 *
 * The thin `'use client'` components that call these are:
 *
 *   - `src/app/today/today-workspace-context.tsx`      (/today)
 *   - `src/components/work/work-workspace-context.tsx` (/work)
 *   - `src/app/work/[id]/work-item-workspace-context.tsx` (/work/:id)
 *
 * ## The priority ladder
 *
 * `route` (0) < page (10) < content owner (20). The most specific thing on
 * screen is described first and is the last to be dropped under budget
 * pressure. A new adapter should pick one of these three rungs rather than
 * inventing a number, so precedence stays a property of the KIND of surface
 * rather than of who shipped last.
 *
 * ## What none of them publish
 *
 * No private text, no field the viewer cannot already read on screen, no
 * permission flags, no counts the page had to compute specially. Everything
 * below is a label that is rendered, an id the user could click, or a filter
 * the user set themselves.
 */

import type { FilterState } from '@/lib/filter-state'
import type { WorkspaceFilter, WorkspaceSurfaceSnapshot } from './types'

/** Page-level rung: what route you are on, expressed as a real surface. */
export const WORKSPACE_PAGE_PRIORITY = 10
/** Content-owner rung: the list or record the page is actually about. */
export const WORKSPACE_CONTENT_PRIORITY = 20

/**
 * Every `FilterState` key, and what `describeFilterState` does with it.
 *
 * The `Record<keyof FilterState, ...>` is the entire point: adding a key to
 * `FilterState` is a type error here until someone decides which column it
 * belongs in. Before this existed the default was silent omission, and five
 * keys had taken it — `activityType`, `personFilter`, `activityPersonFilter`,
 * `myTeam` and `groupByDimension` — so a user filtering by those saw the
 * assistant describe a list it could not account for.
 *
 * - `described` — reported as an active filter.
 * - `view` — not a filter; `buildWorkListWorkspaceSurface` publishes it as
 *   `viewState.viewMode` instead, where the model can read it as presentation
 *   rather than as a narrowing of the data.
 * - `omitted` — deliberately not reported. `groupByDimension` is a grouping
 *   key, not a filter: it changes how rows are arranged, not which rows are
 *   there, and reporting it as a filter would imply records were excluded.
 *   `following` is omitted because item follows are retired (#16026): no read
 *   applies it any more, so a stale `?following=true` excludes nothing.
 */
export const FILTER_STATE_DISPOSITION: Record<
  keyof FilterState,
  'described' | 'view' | 'omitted'
> = {
  search: 'described',
  itemType: 'described',
  activityType: 'described',
  groupIds: 'described',
  personFilter: 'described',
  activityPersonFilter: 'described',
  status: 'described',
  user: 'described',
  captain: 'described',
  following: 'omitted',
  contributing: 'described',
  state: 'described',
  view: 'view',
  scopeToItem: 'described',
  myTeam: 'described',
  tagIds: 'described',
  groupByDimension: 'omitted',
}

/**
 * How an id-valued filter is described: by how many ids it holds, never by
 * the ids.
 *
 * A raw `groupId` or `tagId` is not something the user can see — the UI shows
 * the AOR's or tag's NAME — so publishing the id breaks the visible-only
 * contract this registry rests on, and gives the model a token it can only
 * either ignore or echo back at the user as gibberish. The count is the part
 * that is both true and useful: it tells the assistant the list is narrowed
 * and by how much, without inventing a name it does not have.
 */
function idSelection(count: number): string[] {
  return [count === 1 ? '1 selected' : `${count} selected`]
}

/**
 * Describe an active `FilterState` the way the user set it.
 *
 * Only filters that are ON appear. An exhaustive dump including every unset
 * filter would spend the whole budget saying nothing, and would read to the
 * model as though the user had made choices they did not make.
 */
export function describeFilterState(filters: FilterState): WorkspaceFilter[] {
  const described: WorkspaceFilter[] = []

  if (filters.search?.trim()) {
    described.push({
      key: 'search',
      label: 'Search',
      values: [filters.search.trim()],
    })
  }
  if (filters.status?.length) {
    described.push({ key: 'status', label: 'Status', values: filters.status })
  }
  if (filters.state?.length) {
    described.push({ key: 'state', label: 'State', values: filters.state })
  }
  if (filters.itemType?.length) {
    described.push({
      key: 'itemType',
      label: 'Item type',
      values: filters.itemType,
    })
  }
  if (filters.activityType?.length) {
    described.push({
      key: 'activityType',
      label: 'Activity type',
      values: filters.activityType,
    })
  }
  if (filters.personFilter?.length) {
    described.push({
      key: 'personFilter',
      label: 'Captains',
      values: filters.personFilter,
    })
  }
  if (filters.user) {
    described.push({
      key: 'user',
      label: 'Owner',
      // Anything other than the 'me' sentinel is a person id, which the user
      // never sees — the UI shows the person's name.
      values: [
        filters.user === 'me' ? 'the current user' : 'one specific person',
      ],
    })
  }
  if (filters.activityPersonFilter) {
    const value = filters.activityPersonFilter
    described.push({
      key: 'activityPersonFilter',
      label: 'Activity by',
      values: [
        value === 'all'
          ? 'everyone'
          : value === 'mine'
            ? 'the current user'
            : value === 'not-mine'
              ? 'everyone except the current user'
              : 'one specific person',
      ],
    })
  }
  if (filters.captain) {
    described.push({ key: 'captain', label: 'Captained by me', values: [] })
  }
  if (filters.contributing) {
    described.push({
      key: 'contributing',
      label: 'Contributed to by me',
      values: [],
    })
  }
  if (filters.myTeam) {
    described.push({
      key: 'myTeam',
      label: 'My team only',
      values: [],
    })
  }
  if (filters.groupIds?.length) {
    described.push({
      key: 'groupIds',
      label: 'AORs',
      values: idSelection(filters.groupIds.length),
    })
  }
  if (filters.tagIds?.length) {
    described.push({
      key: 'tagIds',
      label: 'Tags',
      values: idSelection(filters.tagIds.length),
    })
  }
  if (filters.scopeToItem) {
    described.push({
      key: 'scopeToItem',
      label: 'Scoped to one item and its children',
      values: [],
    })
  }

  return described
}

/**
 * `/today` — the composition as a whole.
 *
 * Publishes no records: the shell is a frame, and the elements inside it own
 * what they rendered (see `buildTodayFocusWorkspaceSurface`). Publishing the
 * page's element NAMES as though they were records is how a grounding block
 * starts describing chrome.
 */
export function buildTodayWorkspaceSurface(input: {
  orgName: string
  /** IANA zone the page's dates are rendered in — answers "today, when?". */
  timezone: string
}): WorkspaceSurfaceSnapshot {
  return {
    surfaceId: 'today',
    kind: 'route',
    label: `Today — ${input.orgName}`,
    priority: WORKSPACE_PAGE_PRIORITY,
    route: '/today',
    viewState: { timezone: input.timezone },
    capabilities: [
      { id: 'ask', label: 'Ask a question about the morning in the ask bar' },
      { id: 'focus', label: "Read and steer Today's Focus" },
      { id: 'schedule', label: "See today's meetings in the schedule rail" },
    ],
    // #12949 — the morning's one INVOCABLE capability. Unparented on
    // purpose: `/today` is where a leader notices something they have to do,
    // and the page has no single record to hang it under. A capability that
    // guessed a parent would be putting the assistant's inference into the
    // user's task list.
    actions: [
      { capability: 'workspace.create_task', label: 'Add a task for me' },
    ],
  }
}

/** One ranked item as Today's Focus renders it. */
export interface TodayFocusVisibleItem {
  id: string
  kind: string
  header: string
}

/**
 * `/today` — the ranked focus list.
 *
 * The `failed` flag is published as an explicit filter-ish note rather than by
 * omitting the surface: a failed read and an empty morning are different
 * claims, and the widget already refuses to conflate them on screen (#12529).
 * Chat must not re-introduce the conflation the UI took care to avoid.
 */
export function buildTodayFocusWorkspaceSurface(input: {
  items: TodayFocusVisibleItem[]
  failed: boolean
}): WorkspaceSurfaceSnapshot {
  return {
    surfaceId: 'today-focus',
    kind: 'list',
    label: input.failed
      ? "Today's Focus (could not be built)"
      : "Today's Focus",
    priority: WORKSPACE_CONTENT_PRIORITY,
    route: '/today',
    visibleEntities: input.items.map(item => ({
      id: item.id,
      type: `focus-item:${item.kind}`,
      label: item.header,
    })),
    visibleTotal: input.items.length,
    capabilities: [
      { id: 'steer', label: 'Ask for more or fewer items of a kind' },
      { id: 'open', label: 'Open the record behind an item' },
    ],
    // #12949 — deliberately NO registered actions. A focus item's id is
    // `${kind}:${sourceId}`, a composite this layer invented for rendering
    // and no server-side resolver accepts, so `workspace.open_record` would
    // refuse every one of them. "Open the record behind an item" stays a
    // descriptive capability the assistant may talk about; making it
    // invocable needs the item to publish the underlying record's own id,
    // which is a change to what the widget knows, not to this list.
  }
}

/** One rock as the work list renders it. */
export interface WorkListVisibleRock {
  id: string
  title: string
  status?: string | null
}

/**
 * `/work` — the filtered rock list.
 *
 * BOTH inputs are post-filter. `visibleRocks` is what survived CLIENT-side
 * filtering; `totalCount` is how many survived it in total, which is only
 * larger when `MAX_VISIBLE_ENTITIES` cut the list. `types.ts` defines
 * `visibleTotal` as "how many records match AFTER filtering" and `prompt.ts`
 * renders it as "showing N of TOTAL after filtering", so passing the
 * pre-filter count here made the assistant state a number that was false in
 * the one direction the user cannot check — it implied records matched the
 * filter that did not.
 */
export function buildWorkListWorkspaceSurface(input: {
  visibleRocks: WorkListVisibleRock[]
  totalCount: number
  filters: FilterState
}): WorkspaceSurfaceSnapshot {
  return {
    surfaceId: 'work-list',
    kind: 'list',
    label: 'Rocks',
    priority: WORKSPACE_CONTENT_PRIORITY,
    route: '/work',
    visibleEntities: input.visibleRocks.map(rock => ({
      id: rock.id,
      type: 'rock',
      label: rock.title,
      ...(rock.status ? { status: rock.status } : {}),
    })),
    visibleTotal: input.totalCount,
    filters: describeFilterState(input.filters),
    viewState: input.filters.view
      ? { viewMode: input.filters.view }
      : undefined,
    capabilities: [
      { id: 'filter', label: 'Filter these rocks by status, owner or AOR' },
      { id: 'open', label: 'Open a rock to see its updates and tasks' },
    ],
    // #12949 — read-only, and that is the whole registration. The list is
    // where a user says "open that one"; creating work from a list means
    // guessing which row it belongs to, which is exactly the ambiguity AC6
    // sends to a confirmation rather than to a default.
    actions: [
      { capability: 'workspace.open_record', label: 'Open one of these rocks' },
    ],
  }
}

/** A child item or task listed on a work-item detail page. */
export interface WorkItemRelatedRecord {
  id: string
  type: string
  label: string
  status?: string | null
}

/**
 * `/work/:id` — the detail surface.
 *
 * This is the adapter that outranks the base route registration's guess: the
 * route layer knows only `work-item` and an id, while this one has the title
 * and the status the user is reading. Same entity, better description, higher
 * priority — which is exactly what the precedence rule is for.
 */
export function buildWorkItemWorkspaceSurface(input: {
  id: string
  /** 'rock' | 'project' — whichever `findWorkItem` resolved. */
  itemType: string
  title: string
  status?: string | null
  related: WorkItemRelatedRecord[]
}): WorkspaceSurfaceSnapshot {
  return {
    surfaceId: 'work-item-detail',
    kind: 'detail',
    label: `${input.itemType === 'project' ? 'Project' : 'Rock'} detail`,
    priority: WORKSPACE_CONTENT_PRIORITY,
    route: `/work/${input.id}`,
    entity: {
      id: input.id,
      type: input.itemType,
      label: input.title,
      ...(input.status ? { status: input.status } : {}),
    },
    visibleEntities: input.related.map(record => ({
      id: record.id,
      type: record.type,
      label: record.label,
      ...(record.status ? { status: record.status } : {}),
    })),
    visibleTotal: input.related.length,
    capabilities: [
      { id: 'update', label: 'Post an update on this item' },
      { id: 'tasks', label: 'See and add tasks under this item' },
    ],
    // #12949 — the richest registration, because this is the surface where
    // "this" is unambiguous: the subject is one record and the visible list
    // is its own children and tasks. Both writes carry a confirmation policy
    // set by the server registry, and `open_record` is refused for a task
    // target by the capability's own `targetTypes` rather than by anything
    // this adapter has to remember.
    actions: [
      { capability: 'workspace.create_task', label: 'Add a task here' },
      {
        capability: 'workspace.update_task_status',
        label: 'Change a task’s status',
      },
      { capability: 'workspace.open_record', label: 'Open a child item' },
    ],
  }
}

/** Fixed id for the AI Chat panel's own registration (#16109). */
export const AI_CHAT_WORKSPACE_SURFACE_ID = 'ai-chat'

/**
 * The AI Chat itself (#16109) — published by `AiChatPanel`, which is mounted
 * on every route that has chat (docked, or suppressed under Home's in-page
 * conversation), so the capabilities are offered wherever the person chats.
 *
 * Saving to a note is something the CHAT does with the conversation, not
 * something a page offers about its records: `note.create` takes no target,
 * and `note.add_to` names its note in the input, re-resolved by the handler.
 * So the surface publishes no records — only the two actions.
 *
 * Page rung, not content: a panel layered over the page is not the thing the
 * page is about, and the prompt should describe the page's own records first.
 */
export function buildAiChatWorkspaceSurface(): WorkspaceSurfaceSnapshot {
  return {
    surfaceId: AI_CHAT_WORKSPACE_SURFACE_ID,
    kind: 'panel',
    label: 'AI Chat',
    priority: WORKSPACE_PAGE_PRIORITY,
    actions: [
      { capability: 'note.create', label: 'Save to a new note' },
      { capability: 'note.add_to', label: 'Add to one of my notes' },
    ],
  }
}
